import { useEffect, useMemo, useState } from "react";
import { Period } from "../types";
import { fetchPeriods, savePeriods } from "../services/periodsService";
import { PeriodFieldErrors, validatePeriods } from "../components/periods/periodValidation";

// The backend sends conflict reasons (e.g. "period used by grade 3") as a plain-text body.
const messageOf = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  return typeof data === "string" && data.trim() ? data : fallback;
};

/** A row on screen: the period plus a key that stays stable before it has an id. */
export type PeriodRow = Period & { key: string };

let nextKey = 0;
const toRow = (period: Period): PeriodRow => ({ ...period, key: `row-${nextKey++}` });

const sameRow = (a: PeriodRow, b: PeriodRow) =>
  a.name === b.name && a.startTime === b.startTime && a.endTime === b.endTime && a.isBreak === b.isBreak;

/**
 * The bell schedule. Edits stay in `rows` until `save`, which validates, then replaces the whole
 * schedule in one request; `saved` is the last state the server confirmed.
 */
export const usePeriods = () => {
  const [saved, setSaved] = useState<PeriodRow[]>([]);
  const [rows, setRows] = useState<PeriodRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // shown only after a save attempt, so a half-typed new row isn't red straight away
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const loaded = (await fetchPeriods()).map(toRow);
        setSaved(loaded);
        setRows(loaded);
      } catch (err) {
        setError("Failed to load the bell schedule.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const errors: Record<number, PeriodFieldErrors> = useMemo(
    () => (showErrors ? validatePeriods(rows) : {}),
    [rows, showErrors]
  );

  /** Keys of rows that are new or differ from the saved schedule. */
  const changedKeys = useMemo(
    () =>
      rows
        .filter((row) => {
          const before = saved.find((s) => s.key === row.key);
          return !before || !sameRow(before, row);
        })
        .map((row) => row.key),
    [rows, saved]
  );
  const removedCount = saved.filter((s) => !rows.some((row) => row.key === s.key)).length;
  const dirty = changedKeys.length > 0 || removedCount > 0;

  /** A new row starting when the last period ends, 40 minutes long. */
  const add = () => {
    const last = [...rows].sort((a, b) => a.endTime.localeCompare(b.endTime)).pop();
    const start = last?.endTime || "08:00";
    const [h, m] = start.split(":").map(Number);
    const endMinutes = Math.min(h * 60 + m + 40, 23 * 60 + 59);
    const end = `${String(Math.floor(endMinutes / 60)).padStart(2, "0")}:${String(endMinutes % 60).padStart(2, "0")}`;
    const lessons = rows.filter((row) => !row.isBreak).length;
    setRows((prev) => [
      ...prev,
      toRow({ periodNumber: prev.length + 1, name: `P${lessons + 1}`, startTime: start, endTime: end, isBreak: false }),
    ]);
  };

  const update = (key: string, changes: Partial<Period>) =>
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...changes } : row)));

  const remove = (key: string) => setRows((prev) => prev.filter((row) => row.key !== key));

  const discard = () => {
    setRows(saved);
    setShowErrors(false);
    setError(null);
  };

  /** Returns true when saved; false when invalid (errors shown on the rows) or the server refused. */
  const save = async (): Promise<boolean> => {
    setShowErrors(true);
    if (Object.keys(validatePeriods(rows)).length > 0) return false;
    setSaving(true);
    setError(null);
    try {
      const request = rows.map(({ key, ...period }) => ({ ...period, name: period.name.trim() }));
      const result = (await savePeriods(request)).map(toRow);
      setSaved(result);
      setRows(result);
      setShowErrors(false);
      return true;
    } catch (err) {
      setError(messageOf(err, "Failed to save the bell schedule. Your changes are still on screen."));
      return false;
    } finally {
      setSaving(false);
    }
  };

  return { rows, errors, changedKeys, dirty, loading, saving, error, actions: { add, update, remove, discard, save } };
};
